import React from 'react';
import { startOfMonth, startOfWeek, endOfWeek, endOfMonth, eachDayOfInterval, format, isToday } from 'date-fns';

import { ResourceBookingSchema } from "../data/types";
import { darkenColor, getBookingStatusColor } from "../utils/helpers";

type MonthViewProps = {
  currentDate: Date
  bookings: ResourceBookingSchema[]
  onBookingClick: (booking: ResourceBookingSchema) => void
}

const MonthView: React.FC<MonthViewProps> = ({ bookings, currentDate, onBookingClick }) => {
  const firstDayOfMonth = startOfWeek(startOfMonth(currentDate));
  const lastDayOfMonth = endOfWeek(endOfMonth(currentDate));
  const daysInMonth = eachDayOfInterval({ start: firstDayOfMonth, end: lastDayOfMonth });

  const areDatesInSameMonth = (date1: Date, date2: Date) => {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth();
  };

  // Split days into weeks for row rendering
  const weeks: Date[][] = [];
  for (let i = 0; i < daysInMonth.length; i += 7) {
    weeks.push(daysInMonth.slice(i, i + 7));
  }

  return (
    <div className="overflow-x-auto border-[1px] border-[#E9E9E9] rounded-lg m-4">
      {/* Header Row */}
      <div className="grid grid-cols-7 gap-px mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div key={day} className="bg-white border-r font-raleway border-[#E9E9E9] text-gray-600 text-left p-2 font-light text-md rounded-t">
            {day}
          </div>
        ))}
      </div>
      {/* Weeks */}
      <div className="flex flex-col gap-px">
        {weeks.map((week, weekIdx) => (
          <div key={weekIdx} className="grid grid-cols-7 gap-px">
            {week.map((day) => {
              const isCurrentMonth = areDatesInSameMonth(day, currentDate);
              const isCurrentDay = isToday(day);
              return (
                <div
                  key={day.toISOString()}
                  className={`relative font-raleway min-h-[90px] h-24 flex flex-col border-r border-b border-[#E9E9E9] bg-white px-2 pt-2 pb-1 transition
                    ${!isCurrentMonth ? 'bg-gray-50 text-gray-300' : ''}
                    ${isCurrentDay ? 'bg-primary-200' : ''}
                  `}
                  style={{
                    background: isCurrentDay ? '#003847' : '',
                  }}
                >
                  {/* Day number */}
                  <div className={`absolute top-2 left-2 text-lg font-normal select-none ${isCurrentDay ? 'text-white' : isCurrentMonth ? 'text-gray-800' : 'text-gray-300'}`}>{format(day, 'd')}</div>
                  {/* Bookings */}
                  <div className="flex flex-col gap-1 mt-6">
                    {bookings && bookings.length > 0 && bookings.map((booking) => {
                      const eventStartDate = new Date(booking.start_time || "");
                      const eventEndDate = new Date(booking.end_time || "");
                      if (
                        isNaN(eventStartDate.getTime()) ||
                        isNaN(eventEndDate.getTime())
                      ) return null;
                      if (
                        eventStartDate.getDate() === day.getDate() &&
                        eventStartDate.getMonth() === day.getMonth() &&
                        eventStartDate.getFullYear() === day.getFullYear()
                      ) {
                        const color = booking?.status ? getBookingStatusColor(booking.status) : { bgColor: darkenColor('#38b2ac', 50), primaryTextColor: darkenColor('#38b2ac', 90) };
                        return (
                          <button
                            key={booking.id || `${booking.start_time}-${booking.resource_id}-${day.toISOString()}`}
                            className={`w-full text-xs rounded px-2 py-1 text-left truncate font-medium cursor-pointer focus:outline-none transition border border-transparent hover:border-primary-400 hover:shadow-md`}
                            style={{
                              // background: isCurrentDay ? '' : color.bgColor,
                              color: isCurrentDay ? color.bgColor : color.primaryTextColor,
                            }}
                            title={booking.summary || `${booking.resource_name} for ${booking.user_full_name || booking.username}`}
                            onClick={() => onBookingClick(booking)}
                          >
                            <span className="block font-semibold">{booking.summary || `${booking.resource_name} for ${booking.user_full_name || booking.username}`}</span>
                            <span className="block text-[11px] opacity-80">{`${format(eventStartDate, 'h:mm a')} - ${format(eventEndDate, 'h:mm a')}`}</span>
                          </button>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MonthView;
